import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Stack,
} from "@mui/material";
import {
  History as HistoryIcon,
  FiberManualRecord as DotIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import moment from "moment";
import useProtocolHistory from "../../../../../hooks/useProtocolHistory";

const TimelineLine = styled(Box)(({ theme }) => ({
  position: "absolute",
  left: 5,
  top: 20,
  bottom: 0,
  width: 1,
  backgroundColor: theme.palette.divider,
}));

const DateGroup = styled(Typography)(({ theme }) => ({
  fontSize: 11,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: theme.palette.text.disabled,
  marginBottom: theme.spacing(0.5),
  marginLeft: 24,
}));

function groupByDate(history) {
  const groups = [];
  let currentDate = null;
  let currentGroup = null;

  for (const entry of history) {
    const date = entry?.protocolChanged?.toDate?.();
    const dateKey = date ? moment(date).format("YYYY-MM-DD") : "unknown";
    if (dateKey !== currentDate) {
      currentDate = dateKey;
      currentGroup = { dateKey, date, entries: [] };
      groups.push(currentGroup);
    }
    currentGroup.entries.push(entry);
  }
  return groups;
}

const ProtocolHistoryDialog = ({ open, client, organizationId, onDialogClosed }) => {
  const { protocolHistory, loading } = useProtocolHistory({
    uid: client?.uid,
    organizationId,
    limit: 50,
  });

  const groups = React.useMemo(
    () => groupByDate(protocolHistory),
    [protocolHistory],
  );

  const handleClose = () => onDialogClosed?.();

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ pb: 0.5 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <HistoryIcon fontSize="small" color="action" />
          <Box>
            <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2}>
              Plan History
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {client?.firstName} {client?.lastName}
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ pt: 1.5, px: 2.5 }}>
        {loading ? (
          <Box display="flex" alignItems="center" justifyContent="center" py={4} gap={1.5}>
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">
              Loading…
            </Typography>
          </Box>
        ) : groups.length === 0 ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            py={4}
            gap={1}
          >
            <HistoryIcon sx={{ fontSize: 36, color: "text.disabled" }} />
            <Typography variant="body2" color="text.secondary" textAlign="center">
              No training plan changes recorded yet.
            </Typography>
          </Box>
        ) : (
          <Box>
            {groups.map((group, gi) => (
              <Box key={group.dateKey} sx={{ mb: 1.5 }}>
                <DateGroup>
                  {group.date
                    ? moment(group.date).format("MMM D, YYYY")
                    : "Unknown date"}
                </DateGroup>

                {group.entries.map((entry, ei) => {
                  const isLast =
                    gi === groups.length - 1 &&
                    ei === group.entries.length - 1;
                  return (
                    <Box
                      key={entry.id || `${group.dateKey}-${ei}`}
                      sx={{ position: "relative", pl: 3, pb: 0.75 }}
                    >
                      {!isLast && <TimelineLine />}
                      <DotIcon
                        sx={{
                          position: "absolute",
                          left: 0,
                          top: 3,
                          fontSize: 11,
                          color: "primary.main",
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{ fontSize: 13, lineHeight: 1.4, color: "text.primary" }}
                      >
                        {entry?.name || "Unnamed plan"}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.5 }}>
        <Button onClick={handleClose} size="small" variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProtocolHistoryDialog;
